import { TextField } from '@mui/material';
import { Field, Formik } from 'formik';
import React from 'react';

import { Autocomplete } from '@sbu/components.autocomplete';
import { RowActionConfig } from '@sbu/components.datagrid';
import {
  DataGridAsideActions,
  DataGridAsideAttribute,
  DataGridAsideContent,
  READ_ONLY_FIELD_TOOLTIP,
} from '@sbu/components.datagridaside';
import { useToasts } from '@sbu/contexts.toasts';

import {
  CreateInsightResolutionMutationOperationMutation as CreateInsightResolutionMutationOperation,
  CreateInsightResolutionMutationOperationMutationVariables as CreateInsightResolutionMutationOperationVariables,
  UpdateInsightResolutionMutationOperationMutation as UpdateInsightResolutionMutationOperation,
  UpdateInsightResolutionMutationOperationMutationVariables as UpdateInsightResolutionMutationOperationVariables,
} from 'core/graphql';
import { DataGridAsideForm } from 'shared/DataGridAsideForm';
import { useMutation } from 'shared/hooks';
import { trimWhitespaceObjectValues } from 'shared/utils';

import { useInsightResolutionsContext } from './InsightResolutionsContext';
import {
  CREATE_INSIGHT_RESOLUTION_MUTATION,
  INSIGHT_RESOLUTIONS_QUERY,
  UPDATE_INSIGHT_RESOLUTION_MUTATION,
} from './queries';
import { InsightResolutionRow } from './types';
import { getParentResolution, getParentResolutionOptions } from './utils';
import { validationSchema } from './validation';

export interface InsightResolutionsAsideFormProps {
  parentResolutions?: InsightResolutionRow[];
  rowActions?: RowActionConfig<InsightResolutionRow>[];
}

export const InsightResolutionsAsideForm = ({
  parentResolutions,
  rowActions,
}: InsightResolutionsAsideFormProps) => {
  const { addSuccess } = useToasts();

  const { asideState, selectedRow, onCloseAside, onSaveAside } =
    useInsightResolutionsContext();

  const [createResolution, { loading: creating }] = useMutation<
    CreateInsightResolutionMutationOperation,
    CreateInsightResolutionMutationOperationVariables
  >(CREATE_INSIGHT_RESOLUTION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: INSIGHT_RESOLUTIONS_QUERY }],
  });

  const [updateResolution, { loading: updating }] = useMutation<
    UpdateInsightResolutionMutationOperation,
    UpdateInsightResolutionMutationOperationVariables
  >(UPDATE_INSIGHT_RESOLUTION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: INSIGHT_RESOLUTIONS_QUERY }],
  });

  const parentResolution = getParentResolution(selectedRow, parentResolutions);
  const parentResolutionOptions = getParentResolutionOptions(parentResolutions);

  const initialValues = selectedRow ?? {
    id: -1,
    name: '',
    description: '',
    parentId: null,
  };

  const handleCreate = async ({ id: _id, ...values }: InsightResolutionRow) => {
    const response = await createResolution({
      variables: { fields: trimWhitespaceObjectValues(values) },
    });

    const updatedRow = response.data?.createInsightResolution?.data;
    updatedRow && onSaveAside(updatedRow);

    !response.errors &&
      addSuccess('The insight resolution was created successfully');
  };

  const handleUpdate = async (values: InsightResolutionRow) => {
    if (selectedRow?.id || selectedRow?.id === 0) {
      const response = await updateResolution({
        variables: {
          fields: trimWhitespaceObjectValues({
            description: values?.description,
          }),
          id: selectedRow.id,
        },
      });

      const updatedRow = response.data?.updateInsightResolution?.data;
      updatedRow && onSaveAside({ ...values, ...updatedRow });

      !response.errors &&
        addSuccess('The insight resolution was created successfully');
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={asideState === 'create' ? handleCreate : handleUpdate}
      validateOnMount
      validationSchema={validationSchema}
    >
      {({ isSubmitting }) => (
        <DataGridAsideForm<InsightResolutionRow> autoComplete='off'>
          <DataGridAsideActions
            state={asideState}
            rowActions={rowActions}
            row={selectedRow}
            loading={creating || updating}
            onCancel={onCloseAside}
          />
          <DataGridAsideContent>
            {asideState === 'create' && (
              <>
                <Field name='name'>
                  {({ field, meta: { error, touched } }) => (
                    <TextField
                      data-sumo-test='create-insight-resolution-input-name'
                      disabled={isSubmitting}
                      error={!!error && touched && !isSubmitting}
                      helperText={touched && error}
                      label='Name'
                      required
                      {...field}
                    />
                  )}
                </Field>
                <Field name='parentId'>
                  {({
                    field: { value },
                    form: { setFieldValue },
                    meta: { error, touched },
                  }) => {
                    return (
                      <Autocomplete
                        disableClearable
                        disabled={isSubmitting}
                        TextFieldProps={{
                          'data-sumo-test': 'create-insight-resolution-select',
                          error: !!error && touched && !isSubmitting,
                          helperText: touched && error,
                          label: 'Parent Resolution',
                          required: true,
                        }}
                        fullWidth
                        onChange={(_e, v) => setFieldValue('parentId', v.value)}
                        value={parentResolutionOptions.find(
                          (o) => o.value === value
                        )}
                        options={parentResolutionOptions}
                      />
                    );
                  }}
                </Field>
              </>
            )}
            {asideState === 'update' && (
              <DataGridAsideAttribute
                data-sumo-test='insight-resolution-parent'
                label='Parent Resolution'
                value={parentResolution?.name}
                tooltip={READ_ONLY_FIELD_TOOLTIP}
              />
            )}
            <Field name='description'>
              {({ field }) => (
                <TextField
                  data-sumo-test='create-insight-resolution-input-description'
                  disabled={isSubmitting}
                  label='Description'
                  {...field}
                />
              )}
            </Field>
          </DataGridAsideContent>
        </DataGridAsideForm>
      )}
    </Formik>
  );
};
